import styled from "@emotion/styled";
import { ContactBriefingView } from "@features/Contacts";
import { usePageNavbar } from "@shared/components/usePageNavbar";
import { Skeleton, Typography } from "@avenue-8/sidekick-ui";
import { useMemo } from "react";
import { ZodError } from "zod/v4";
import { useLocation, useParams } from "react-router";
import { toBriefing } from "@features/Contacts/contacts-helpers";
import { ContactsLayout } from "@features/Contacts/layouts/ContactsLayout";
import { withMainLayout } from "@shared/layouts/withMainLayout";
import { getContactBriefingBackTo } from "@shared/utils/contact-briefing-links";
import { useGetContact } from "@shared/generated/crm/queries/contactsController/useGetContact";
import {
  getContactPathParamsSchema,
  getContactQueryResponseSchema,
} from "@shared/generated/crm/zod/contactsController/getContactSchema";

const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
  padding: 1rem 1rem 1rem;

  ${(p) => p.theme.media.mobileDown} {
    padding: 0 0.75rem 1rem;
  }
`;

function ContactBriefingPageContent() {
  const rawParams = useParams<{ contactId: string }>();
  const location = useLocation();
  const paramsParsed = getContactPathParamsSchema.safeParse(rawParams);

  const {
    data: contact,
    isLoading,
    isError,
    error,
  } = useGetContact(
    paramsParsed.success ? paramsParsed.data.contactId : "",
    undefined,
    {
      query: {
        enabled: paramsParsed.success,
        select: (data) => getContactQueryResponseSchema.parse(data),
      },
    },
  );

  const briefing = useMemo(
    () => (contact ? toBriefing(contact) : null),
    [contact],
  );

  const header = usePageNavbar({
    title: "Contact Briefing",
    subtitle: briefing?.header.name,
    showBackButton: true,
    backTo: getContactBriefingBackTo(location.state),
  });
  const withLayout = (content: React.ReactNode) => (
    <ContactsLayout header={header}>{content}</ContactsLayout>
  );

  if (isLoading) {
    return withLayout(
      <SkeletonContainer>
        <Skeleton variant="rounded" height={180} />
        <Skeleton variant="text" width={120} sx={{ marginTop: 3 }} />
        <Skeleton variant="rounded" height={300} width={"100%"} />
      </SkeletonContainer>,
    );
  }

  if (!paramsParsed.success) {
    return withLayout(
      <Typography textAlign={"center"} mt={2} p={2}>
        This contact link is invalid.
      </Typography>,
    );
  }

  if (isError) {
    const message =
      error instanceof ZodError
        ? "Something went wrong on our end."
        : "Could not load contact. Please try again.";
    console.error("Error loading contact", error);
    return withLayout(
      <Typography textAlign={"center"} mt={2} p={2}>
        {message}
      </Typography>,
    );
  }

  if (!briefing) {
    return withLayout(
      <Typography textAlign={"center"} mt={2} p={2}>
        Contact not found.
      </Typography>,
    );
  }

  return withLayout(<ContactBriefingView briefing={briefing} />);
}

export const ContactBriefingPage = withMainLayout(ContactBriefingPageContent);
